import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { certificateService, userGroupService } from '../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Divider,
  Chip,
  Avatar,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Security as CertificateIcon,
  Dns as DnsIcon,
  VpnKey as KeyIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  Inventory as InventoryIcon,
  Comment as CommentIcon,
  Code as CodeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';

const CertificateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    certificate_type: '',
    serial_number: '',
    inventory_number: '',
    dns_name: '',
    dns_suffix: '',
    certificate_request: '',
    certificate_content: '',
    command_used: '',
    is_self_signed: false,
    expiration_date: '',
    comments: '',
    status: 'active',
    location: '',
    technician_in_charge: '',
    group_in_charge: '',
    user_group: '',
    permission_group_ids: [],
    // Novos campos para certificados digitais
    document_type: '',
    document_id: '',
    order_number: '',
    certificate_model: '',
    legal_responsible: '',
    issue_date: '',
    entity: '',
    users: '',
    softwares: '',
    intermediary: '',
    intermediary_contact: '',
    certificadora: '',
    purchase_request: '',
    tutorial: '',
    ownership_term: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isEdit, setIsEdit] = useState(false);
  const [groups, setGroups] = useState([]);

  // Opções para selects
  const certificateTypeOptions = [
    'SSL/TLS',
    'Wildcard',
    'Multi-domain',
    'EV (Extended Validation)',
    'OV (Organization Validation)',
    'DV (Domain Validation)',
    'Code Signing',
    'Email (S/MIME)',
    'Client Certificate'
  ];

  const statusOptions = [
    { value: 'active', label: '🟢 Ativo' },
    { value: 'inactive', label: '🔴 Inativo' },
    { value: 'expired', label: '⏰ Expirado' },
    { value: 'revoked', label: '🚫 Revogado' },
    { value: 'maintenance', label: '🟡 Manutenção' }
  ];

  useEffect(() => {
    loadGroups();
    if (id) {
      loadCertificate(id);
    }
  }, [id]);

  const loadGroups = async () => {
    try {
      const response = await userGroupService.getUserGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const loadCertificate = async (certificateId) => {
    try {
      setLoading(true);
      const response = await certificateService.getCertificate(certificateId);
      const certificate = response.certificate;

      setFormData({
        name: certificate.name || '',
        certificate_type: certificate.certificate_type || '',
        serial_number: certificate.serial_number || '',
        inventory_number: certificate.inventory_number || '',
        dns_name: certificate.dns_name || '',
        dns_suffix: certificate.dns_suffix || '',
        certificate_request: certificate.certificate_request || '',
        certificate_content: certificate.certificate_content || '',
        command_used: certificate.command_used || '',
        is_self_signed: certificate.is_self_signed || false,
        expiration_date: certificate.expiration_date ? certificate.expiration_date.split('T')[0] : '',
        comments: certificate.comments || '',
        status: certificate.status || 'active',
        location: certificate.location || '',
        technician_in_charge: certificate.technician_in_charge || '',
        group_in_charge: certificate.group_in_charge || '',
        user_group: certificate.user_group || '',
        permission_group_ids: certificate.permission_group_ids || (certificate.group_id ? [certificate.group_id] : []),
        // Novos campos para certificados digitais
        document_type: certificate.document_type || '',
        document_id: certificate.document_id || '',
        order_number: certificate.order_number || '',
        certificate_model: certificate.certificate_model || '',
        legal_responsible: certificate.legal_responsible || '',
        issue_date: certificate.issue_date ? certificate.issue_date.split('T')[0] : '',
        entity: certificate.entity || '',
        users: certificate.users || '',
        softwares: certificate.softwares || '',
        intermediary: certificate.intermediary || '',
        intermediary_contact: certificate.intermediary_contact || '',
        certificadora: certificate.certificadora || '',
        purchase_request: certificate.purchase_request || '',
        tutorial: certificate.tutorial || '',
        ownership_term: certificate.ownership_term || ''
      });

      setIsEdit(true);
    } catch (error) {
      console.error('Erro ao carregar certificado:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar certificado',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Processar dados antes de enviar
      const submitData = {
        ...formData
      };

      if (isEdit && id) {
        await certificateService.updateCertificate(id, submitData);
        setSnackbar({
          open: true,
          message: 'Certificado atualizado com sucesso!',
          severity: 'success'
        });
      } else {
        await certificateService.createCertificate(submitData);
        setSnackbar({
          open: true,
          message: 'Certificado criado com sucesso!',
          severity: 'success'
        });
      }

      // Redirecionar após sucesso
      setTimeout(() => {
        navigate('/certificates');
      }, 1500);

    } catch (error) {
      console.error('Erro no formulário:', error);
      setSnackbar({
        open: true,
        message: error.message || error.data?.error || (isEdit ? 'Erro ao atualizar certificado' : 'Erro ao criar certificado'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
              <CertificateIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">
                {isEdit ? 'Editar Certificado' : 'Novo Certificado'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isEdit ? 'Edite as informações do certificado' : 'Cadastre um novo certificado digital'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Informações Básicas */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CertificateIcon sx={{ mr: 1 }} />
                    Informações Básicas
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nome do Certificado"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        placeholder="Ex: Certificado SSL empresa.com, CertificadoWildcard"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Tipo de Certificado</InputLabel>
                        <Select
                          name="certificate_type"
                          value={formData.certificate_type}
                          onChange={handleChange}
                          label="Tipo de Certificado"
                        >
                          {certificateTypeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          label="Status"
                        >
                          {statusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* DNS e Domínio */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <DnsIcon sx={{ mr: 1 }} />
                    DNS e Domínio
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nome DNS"
                        name="dns_name"
                        value={formData.dns_name}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: empresa.com, subdominio.empresa.com"
                        InputProps={{
                          startAdornment: <DnsIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Sufixo DNS"
                        name="dns_suffix"
                        value={formData.dns_suffix}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: .com, .com.br, .local"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.is_self_signed}
                              onChange={handleSwitchChange}
                              name="is_self_signed"
                              disabled={loading}
                            />
                          }
                          label="Certificado Auto-assinado"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Identificadores */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <InventoryIcon sx={{ mr: 1 }} />
                    Identificadores
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Número Serial"
                        name="serial_number"
                        value={formData.serial_number}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Número serial do certificado"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Número do Inventário"
                        name="inventory_number"
                        value={formData.inventory_number}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Número do inventário interno"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Grupos de Permissão</InputLabel>
                        <Select
                          name="permission_group_ids"
                          value={formData.permission_group_ids}
                          onChange={handleChange}
                          label="Grupos de Permissão"
                          multiple
                        >
                          {groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Requisição e Certificado */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <KeyIcon sx={{ mr: 1 }} />
                    CSR e Certificado
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Certificate Signing Request (CSR)"
                        name="certificate_request"
                        value={formData.certificate_request}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Cole aqui o CSR gerado"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Conteúdo do Certificado"
                        name="certificate_content"
                        value={formData.certificate_content}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Cole aqui o certificado completo"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Comando Utilizado"
                        name="command_used"
                        value={formData.command_used}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: openssl req -new -key server.key -out server.csr"
                        InputProps={{
                          startAdornment: <CodeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Certificados Digitais */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CertificateIcon sx={{ mr: 1 }} />
                    Certificados Digitais (e-CNPJ, e-CPF, Procurações)
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Tipo de Documento</InputLabel>
                        <Select
                          name="document_type"
                          value={formData.document_type}
                          onChange={handleChange}
                          label="Tipo de Documento"
                        >
                          <MenuItem value="e-CNPJ">e-CNPJ</MenuItem>
                          <MenuItem value="e-CPF">e-CPF</MenuItem>
                          <MenuItem value="Procuração eletrônica">Procuração Eletrônica</MenuItem>
                          <MenuItem value="Procuração">Procuração</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="ID do Documento"
                        name="document_id"
                        value={formData.document_id}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: CNPJ24499170, CPF24499491, PE20230523"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Número do Pedido"
                        name="order_number"
                        value={formData.order_number}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Número do pedido de compra"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Modelo</InputLabel>
                        <Select
                          name="certificate_model"
                          value={formData.certificate_model}
                          onChange={handleChange}
                          label="Modelo"
                        >
                          <MenuItem value="Arquivo">Arquivo</MenuItem>
                          <MenuItem value="Nuvem">Nuvem</MenuItem>
                          <MenuItem value="Token">Token</MenuItem>
                          <MenuItem value="Cartão">Cartão</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Responsável Legal"
                        name="legal_responsible"
                        value={formData.legal_responsible}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Nome do responsável legal"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Data de Emissão"
                        name="issue_date"
                        value={formData.issue_date}
                        onChange={handleChange}
                        disabled={loading}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Entidade"
                        name="entity"
                        value={formData.entity}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: Dadosfera Brasil, HealthLake"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Quem Usa"
                        name="users"
                        value={formData.users}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Usuários ou setores que utilizam"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Softwares"
                        name="softwares"
                        value={formData.softwares}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Softwares associados"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Intermediário"
                        name="intermediary"
                        value={formData.intermediary}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Nome do intermediário/reseller"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Contato do Intermediário"
                        name="intermediary_contact"
                        value={formData.intermediary_contact}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Telefone/email do intermediário"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Certificadora"
                        name="certificadora"
                        value={formData.certificadora}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Nome da empresa certificadora"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Requisição de Compra"
                        name="purchase_request"
                        value={formData.purchase_request}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Código da requisição de compra"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Tutorial/Link"
                        name="tutorial"
                        value={formData.tutorial}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Link para tutorial ou instruções"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Termo de Titularidade"
                        name="ownership_term"
                        value={formData.ownership_term}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Link ou referência do termo"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Configurações e Expiração */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ mr: 1 }} />
                    Configurações e Expiração
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Data de Expiração"
                        name="expiration_date"
                        value={formData.expiration_date}
                        onChange={handleChange}
                        disabled={loading}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          startAdornment: <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Comentários */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CommentIcon sx={{ mr: 1 }} />
                    Comentários
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Comentários e Observações"
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Adicione comentários, propósito do certificado, restrições..."
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Relacionamentos GLPI */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon sx={{ mr: 1 }} />
                    Relacionamentos e Responsabilidades
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Localização"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Servidor, ambiente, datacenter..."
                        InputProps={{
                          startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Técnico Responsável"
                        name="technician_in_charge"
                        value={formData.technician_in_charge}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Nome do técnico responsável"
                        InputProps={{
                          startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Grupo Responsável"
                        name="group_in_charge"
                        value={formData.group_in_charge}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Nome do grupo/setor responsável"
                        InputProps={{
                          startAdornment: <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Grupo do Usuário"
                        name="user_group"
                        value={formData.user_group}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Grupo de usuários do certificado"
                        InputProps={{
                          startAdornment: <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Informações Adicionais */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CertificateIcon sx={{ mr: 1 }} />
                    Resumo do Certificado
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    {/* Resumo dos campos tradicionais */}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Tipo SSL:</strong> {formData.certificate_type || 'Não definido'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>DNS:</strong> {formData.dns_name ? `${formData.dns_name}${formData.dns_suffix || ''}` : 'Não definido'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Auto-assinado:</strong> {formData.is_self_signed ? 'Sim' : 'Não'}
                    </Typography>

                    {/* Resumo dos campos de certificados digitais */}
                    {formData.document_type && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>📄 Tipo Documento:</strong> {formData.document_type}
                      </Typography>
                    )}
                    {formData.document_id && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>ID Documento:</strong> {formData.document_id}
                      </Typography>
                    )}
                    {formData.entity && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>🏢 Entidade:</strong> {formData.entity}
                      </Typography>
                    )}
                    {formData.certificadora && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>🏛️ Certificadora:</strong> {formData.certificadora}
                      </Typography>
                    )}

                    {formData.expiration_date && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Expira:</strong> {new Date(formData.expiration_date).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" color="text.secondary">
                    💡 Campos obrigatórios: Nome
                    <br />
                    📅 Data de expiração é opcional
                    <br />
                    🔒 CSR e certificado são confidenciais
                    <br />
                    🌐 DNS name é fundamental para validação
                  </Typography>
                </CardContent>
              </Card>

              {/* Ações */}
              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : 'Salvar Certificado'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Snackbar para notificações */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default CertificateForm;
